import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback, useState } from "react";
import { useAccount, useDisconnect } from "wagmi";
import notify from "../components/common/notify";
import {
  selectGeneral,
  setIsLoggedIn,
  setShowAuthModal,
} from "../redux/slices/generalSlice";
import { accessToken } from "../utils/localData";

/**
 * Simplified useAuth hook
 * 
 * Handles authentication state and user session management.
 * Authentication methods: Email/Password, Google OAuth
 * 
 * Note: Wallet connection is handled separately by useWalletConnection hook
 * and is NOT an authentication method.
 */
const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const { isLoggedIn } = useSelector(selectGeneral);
  
  // Wallet connection state (for features, not auth)
  const { address: walletAddress, isConnected: walletConnected } = useAccount();
  const { disconnect: disconnectWallet } = useDisconnect();

  /**
   * Check if user is authenticated and fetch user data
   */
  const checkAuth = useCallback(async () => {
    const token = accessToken();
    
    if (!token) {
      dispatch(setIsLoggedIn(false));
      setUser(null);
      return null;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Only clear token on 401 (unauthorized) - actual auth failures
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          localStorage.removeItem("auth_provider");
          dispatch(setIsLoggedIn(false));
          setUser(null);
        }
        return null;
      }

      const data = await response.json();
      setUser(data.user);
      dispatch(setIsLoggedIn(true));
      return data.user;
    } catch (err) {
      // Network error - don't clear the token
      return null;
    }
  }, [dispatch]);

  /**
   * Login with email and password
   */
  const loginWithEmail = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      // Store token
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("auth_provider", data.user.auth_provider);

      setUser(data.user);
      dispatch(setIsLoggedIn(true));
      notify("Successfully logged in!", "success");

      // Redirect based on profile completion
      if (data.user.profile_step < 1) {
        navigate("/create-profile");
      }

      return data.user;
    } catch (err) {
      notify(err.message, "error");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register with email and password
   */
  const registerWithEmail = async (email, password, firstName, lastName, role) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          role,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      // Store token
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("auth_provider", "email");

      setUser(data.user);
      dispatch(setIsLoggedIn(true));
      notify("Account created successfully! Please check your email to verify.", "success");

      // Redirect to profile completion
      navigate("/create-profile");

      return data.user;
    } catch (err) {
      notify(err.message, "error");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initiate Google OAuth login
   */
  const loginWithGoogle = (redirectAfterLogin = null) => {
    const redirectParam = redirectAfterLogin 
      ? `?redirect=${encodeURIComponent(redirectAfterLogin)}` 
      : "";
    window.location.href = `${process.env.REACT_APP_BASE_URL}/auth/google${redirectParam}`;
  };

  /**
   * Request password reset email
   */
  const forgotPassword = async (email) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset email");
      }

      notify("Password reset email sent. Check your inbox.", "success");
      return true;
    } catch (err) {
      notify(err.message, "error");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset password with token
   */
  const resetPassword = async (token, newPassword) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to reset password");
      }

      notify("Password reset successfully! Please log in.", "success");
      navigate("/");
      return true;
    } catch (err) {
      notify(err.message, "error");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("auth_provider");
    localStorage.removeItem("public_address");
    localStorage.removeItem("signupPopupShown");
    localStorage.removeItem("wallet_data");
    setUser(null);
    dispatch(setIsLoggedIn(false));
    disconnectWallet();
    notify("Successfully logged out", "success");
    navigate("/");
  }, [dispatch, disconnectWallet, navigate]);

  /**
   * Show auth modal
   */
  const showLogin = (redirectAfterLogin = null) => {
    dispatch(setShowAuthModal({ show: true, redirect: redirectAfterLogin }));
  };

  /**
   * Check if user needs to complete profile
   */
  const needsProfileCompletion = useCallback(() => {
    return user && user.profile_step < 2;
  }, [user]);

  /**
   * Check if user has connected wallet
   */
  const hasWallet = useCallback(() => {
    return user && user.wallet_address;
  }, [user]);

  // Check auth on mount and when token changes
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    // State
    user,
    isLoggedIn,
    isLoading,
    walletAddress,
    walletConnected,
    
    // Auth methods
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    forgotPassword,
    resetPassword,
    logout,
    checkAuth,
    showLogin,
    
    // Utilities
    needsProfileCompletion,
    hasWallet,
  };
};

export default useAuth;
